import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule, Routes } from '@angular/router';
import { AppLayoutComponent } from './layout/app.layout.component';
import { AuthGuard } from './services/auth.guard';
import { ArticleAccountingService } from './services/article-accounting.service';
import { productGuard } from './services/product.guard';
import { permissionsGuard } from './services/permissions.guard';
import { PermissionResolver } from './resolvers/permission.service';
const routerOptions: ExtraOptions = {
    anchorScrolling: 'enabled',
};

const routes: Routes = [
    {
        path: 'u/:routeName',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: AppLayoutComponent,
        children: [
            { path: '', redirectTo: 'home', pathMatch: 'full' },
            {
                path: 'home',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: { 
                    breadcrumb: 'Home',
                    permission: 'home',
                    permissionType: 'access',
                 },
                loadChildren: () =>
                    import('./home/home.module').then((m) => m.HomePageModule),
            },
            {
                // Third-party service status (payment providers + toggled payment options) for the unit.
                // No permission check yet — every user of the unit can open it.
                path: 'status',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Status' },
                loadComponent: () =>
                    import('./status/status-page.component').then(
                        (m) => m.StatusPageComponent
                    ),
            },
            {
                path: 'old',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Old' },
                loadChildren: () =>
                    import(
                        './demo/components/dashboards/dashboards.module'
                    ).then((m) => m.DashboardsModule),
            },
            {
                path: 'sales',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: { 
                    breadcrumb: 'Sales',
                    permission: 'sales',
                    permissionType: 'access',
                 },
                loadChildren: () =>
                    import('./sales/sales.module').then((m) => m.SalesModule),
            },
            {
                path: 'menus/m',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Menus' },
                loadChildren: () =>
                    import('./menu/menu.module').then((m) => m.MenuPageModule),
            },
            {
                path: 'usergroups',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'User Groups',
                    permission: 'loyalty',
                    permissionType: 'access',
                },
                loadComponent: () =>
                    import('./user-groups/user-groups.component').then(
                        (m) => m.UserGroups
                    ),
            },
            {
                path: 'accounting',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Accounting',
                    permission: 'settings',
                    permissionType: 'finance',
                },
                loadComponent: () =>
                    import('./accountings/accountings.component').then(
                        (m) => m.AccountingsComponent
                    ),
            },
            {
                path: 'vat',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'VAT',
                    permission: 'settings',
                    permissionType: 'finance',
                },
                loadComponent: () =>
                    import('./vat/vat.component').then((m) => m.VATComponent),
            },
            {
                path: 'app',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'App' },
                loadChildren: () =>
                    import(
                        './prime-app-settings/prime-app-settings.module'
                    ).then((m) => m.PrimeAppSettingsModule),
            },
            {
                path: 'timeslots',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Timeslots',
                    permission: 'app',
                    permissionType: 'timeslots',
                },
                loadComponent: () =>
                    import('./timeslots/timeslots.component').then(
                        (m) => m.TimeslotsComponent
                    ),
            },
            {
                path: 'rules',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Order & Printing Rules' },
                loadComponent: () =>
                    import(
                        './home/bongCategoryActions/bongCategoryActions.component'
                    ).then((m) => m.BongCategoryActionsComponent),
            },
            {
                path: 'guests',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Guests',
                    permission: 'loyalty',
                    permissionType: 'access',
                },
                loadComponent: () =>
                    import(
                        './user-information/user-information.component'
                    ).then((m) => m.UserInformationComponent),
            },
            {
                path: 'settings/timeslots',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Timeslots',
                    permission: 'settings',
                    permissionType: 'timeslots',
                },
                loadComponent: () =>
                    import(
                        './timeslots-configuration/timeslots-configuration.component'
                    ).then((m) => m.TimeslotsConfigurationComponent),
            },
            {
                path: 'settings/delivery',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Delivery' },
                loadComponent: () =>
                    import(
                        './unit-delivery-settings/unit-delivery-settings.component'
                    ).then((m) => m.UnitDeliverySettingsComponent),
            },
            {
                path: 'settings/buttons',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Buttons',
                    permission: 'settings',
                    permissionType: 'appButtons',
                },
                loadComponent: () =>
                    import('./buttons-setting/buttons-setting.component').then(
                        (m) => m.ButtonsSettingComponent
                    ),
            },
            {
                path: 'settings/mfa',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: { breadcrumb: 'MFA' },
                loadComponent: () =>
                    import('./mfa-setting/mfa-setting.component').then(
                        (m) => m.MfaSettingComponent
                    ),
            },
            {
                path: 'settings/payment',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Payment' },
                loadComponent: () =>
                    import(
                        './unit-payment-settings/unit-payment-settings.component'
                    ).then((m) => m.UnitPaymentSettingsComponent),
            },
            {
                path: 'invoice/customers',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Corporate Customers' },
                loadComponent: () =>
                    import(
                        './catering/catering-corporate-customers/catering-corporate-customers.component'
                    ).then((m) => m.CateringCorporateCustomersComponent),
            },
            {
                path: 'invoice/list',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Invoices' },
                loadComponent: () =>
                    import('./invoice/invoice-list/invoice-list.component').then(
                        (m) => m.InvoiceListComponent
                    ),
            },
            {
                path: 'products',

                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Products' },
                loadChildren: () =>
                    import('./products/products.module').then(
                        (m) => m.ProductsModule
                    ),
            },
            {
                path: 'news',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'News' },
                loadChildren: () =>
                    import('./news/news.module').then((m) => m.NewsModule),
            },
            {
                path: 'info',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Restaurant info' },
                loadComponent: () =>
                    import('./unit-settings/unit-settings.component').then(
                        (m) => m.UnitSettingsComponent
                    ),
            },
            {
                path: 'hours',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Opening hours' },
                loadComponent: () =>
                    import(
                        './unit-settings/opening-hours/opening-hours.component'
                    ).then((m) => m.OpeningHoursComponent),
            },
            {
                path: 'table-booking',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Table booking' },
                loadComponent: () =>
                    import(
                        './unit-settings/table-booking-settings/table-booking-page.component'
                    ).then((m) => m.TableBookingPageComponent),
            },
            {
                path: 'payment-options',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Payment Options',
                    permission: 'settings',
                    permissionType: 'finance',
                },
                loadComponent: () =>
                    import('./payment-options/payment-options.component').then(
                        (m) => m.PaymentOptionsComponent
                    ),
            },
            {
                path: 'loyalty',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Loyalty',
                    permission: 'loyalty',
                    permissionType: 'access',
                },
                loadChildren: () =>
                    import('./loyalty/loyalty.module').then(
                        (m) => m.LoyaltyModule
                    ),
            },
            {
                path: 'product-category',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Product Categories' },
                loadComponent: () =>
                    import(
                        './product-category/product-category.component'
                    ).then((m) => m.ProductCategoryComponent),
            },
            {
                path: 'add-ons',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Add-ons' },
                loadChildren: () =>
                    import('./addons/addons.module').then(
                        (m) => m.AddonsModule
                    ),
            },
            {
                path: 'pos',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'POS',
                    permission: 'pos',
                    permissionType: 'access',
                },
                loadChildren: () =>
                    import('./pos/pos.module').then((m) => m.PosModule),
            },
            {
                // Order-validation terminals. Gated on the POS permission rather than
                // one of its own: the terminals belong to the same units and the same
                // people manage them, and a new permission would have to be provisioned
                // on every account before the screen was reachable at all.
                path: 'order-validation',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Order validation',
                    permission: 'pos',
                    permissionType: 'access',
                },
                loadChildren: () =>
                    import('./order-validation/order-validation.module').then(
                        (m) => m.OrderValidationModule
                    ),
            },
            {
                // The organisation area (group-wide stats, shared catalogue, shared
                // coupons, org access). Every child sits behind orgAdminGuard; this is
                // NOT the legacy per-user `organization` module.
                path: 'org',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Organisation' },
                loadChildren: () => import('./org/org.routes').then((m) => m.ORG_ROUTES),
            },
            {
                // Staff module (personalliggare). AuthGuard also requires the hasStaff
                // flag; staffApi enforces the staff permission on every call.
                path: 'staff',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Staff' },
                loadChildren: () =>
                    import('./staff/staff.routes').then((m) => m.STAFF_ROUTES),
            },
            {
                path: 'team',

                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Team' },
                loadComponent: () =>
                    import('./team/team.component').then(
                        (m) => m.TeamComponent
                    ),
            },
            {
                path: 'uikit',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'UI Kit' },
                loadChildren: () =>
                    import('./demo/components/uikit/uikit.module').then(
                        (m) => m.UIkitModule
                    ),
            },
            {
                path: 'utilities',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Utilities' },
                loadChildren: () =>
                    import('./demo/components/utilities/utilities.module').then(
                        (m) => m.UtilitiesModule
                    ),
            },
            {
                path: 'pages',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Pages' },
                loadChildren: () =>
                    import('./demo/components/pages/pages.module').then(
                        (m) => m.PagesModule
                    ),
            },
            {
                path: 'profile',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'User Management' },
                loadChildren: () =>
                    import('./demo/components/profile/profile.module').then(
                        (m) => m.ProfileModule
                    ),
            },
            {
                path: 'documentation',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Documentation' },
                loadChildren: () =>
                    import(
                        './demo/components/documentation/documentation.module'
                    ).then((m) => m.DocumentationModule),
            },
            {
                path: 'blocks',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Prime Blocks' },
                loadChildren: () =>
                    import(
                        './demo/components/primeblocks/primeblocks.module'
                    ).then((m) => m.PrimeBlocksModule),
            },
            {
                path: 'ecommerce',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'E-Commerce' },
                loadChildren: () =>
                    import('./demo/components/ecommerce/ecommerce.module').then(
                        (m) => m.EcommerceModule
                    ),
            },
            {
                path: 'apps',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Apps' },
                loadChildren: () =>
                    import('./demo/components/apps/apps.module').then(
                        (m) => m.AppsModule
                    ),
            },
            {
                path: 'organization',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Organization',
                    permission: 'access',
                    permissionType: 'active',
                },
                loadChildren: () =>
                    import('./organization/organization.module').then(
                        (m) => m.OrganizationModule
                    ),
            },
            {
                path: 'weeklyMenu',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Weekly Menu' },
                loadComponent: () =>
                    import('./weekly-menu/weekly-menu.component').then(
                        (m) => m.WeeklyMenuComponent
                    ),
            },
            {
                path: 'weeklymenusV2',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Weekly Menus V2' },
                loadComponent: () =>
                    import('./weekly-menus-v2/weekly-menus-v2.component').then(
                        (m) => m.WeeklyMenusV2Component
                    ),
            },
            {
                path: 'settings/webordersettings',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Web Order Settings',
                    permission: 'settings',
                    permissionType: 'webOrder',
                },
                loadComponent: () =>
                    import(
                        './web-order-settings/web-order-settings.component'
                    ).then((m) => m.WebOrderSettingsComponent),
            },
            {
                path: 'api-export',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Api export',
                    permission: 'settings',
                    permissionType: 'finance',
                },
                loadComponent: () =>
                    import(
                        './sales-export-api/sales-export-api.component'
                    ).then((m) => m.SalesExportApiComponent),
            },
            {
                // Integrations hub: the add-ons a restaurant activates (Fortnox; Personalkollen and
                // Personalkollen listed as coming soon). New ones: integrations/integrations.registry.ts.
                path: 'settings/integrations',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Integrations',
                    permission: 'settings',
                    permissionType: 'finance',
                },
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('./integrations/integrations.component').then((m) => m.IntegrationsComponent),
                    },
                    {
                        // Z-rapporter -> Fortnox (pubq-functions fortnoxZApi).
                        path: 'fortnox',
                        data: { breadcrumb: 'Fortnox' },
                        loadComponent: () =>
                            import('./integrations/fortnox/fortnox-z.component').then((m) => m.FortnoxZComponent),
                    },
                ],
            },
            // Earlier addresses of these pages (and of earlier OAuth callbacks); query params kept.
            { path: 'integrations', redirectTo: 'settings/integrations', pathMatch: 'full' },
            { path: 'integrations/fortnox', redirectTo: 'settings/integrations/fortnox', pathMatch: 'full' },
            { path: 'fortnox', redirectTo: 'settings/integrations/fortnox', pathMatch: 'full' },
            {
                path: 'reports',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Reports',
                    permission: 'settings',
                    permissionType: 'finance',
                },
                loadComponent: () =>
                    import('./reports/reports.component').then(
                        (m) => m.ReportsComponent
                    ),
            },
            {
                // The screen additionally checks reportScheduleAdmins/{uid}, which is what the
                // Firestore rules on reportSchedules enforce.
                path: 'scheduled-reports',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Scheduled reports',
                    permission: 'settings',
                    permissionType: 'finance',
                },
                loadComponent: () =>
                    import('./scheduled-reports/scheduled-reports-list.component').then(
                        (m) => m.ScheduledReportsListComponent
                    ),
            },
            {
                path: 'settings/self-service-kiosk',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Self-service Kiosk',
                    permission: 'settings',
                    permissionType: 'selfServiceKiosk',
                },
                loadComponent: () =>
                    import(
                        './self-service-kiosk/self-service-kiosk.component'
                    ).then((m) => m.SelfServiceKioskComponent),
            },

            {
                path: 'settings/printers',
                // resolve: {
                //     permissions: PermissionResolver,
                // },
                // canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Printers',
                    // permission: 'settings',
                    // permissionType: 'webOrder',
                },
                loadComponent: () =>
                    import('./printers/printers.component').then(
                        (m) => m.PrintersComponent
                    ),
            },
            {
                path: 'push',
                resolve: {
                    permissions: PermissionResolver,
                },
                data: { breadcrumb: 'Send push notification' },
                loadComponent: () =>
                    import(
                        './push-notifications/push-notifications.component'
                    ).then((m) => m.PushNotificationsComponent),
            },
            {
                path: 'receipts',
                resolve: {
                    permissions: PermissionResolver,
                },
                canActivate: [permissionsGuard],
                data: {
                    breadcrumb: 'Receipts',
                    permission: 'receipts',
                    permissionType: 'access',
                },
                loadComponent: () =>
                    import(
                        './receipt/receipt-list/receipt-list.component'
                    ).then((m) => m.ReceiptListComponent),
            },
            // { path: 'printers', data: { breadcrumb: 'Printers' }, loadComponent: () => import('./printers/printers.component').then(m => m.PrintersComponent) }
            {
                path: 'catering',
                // resolve: {
                //     permissions: PermissionResolver,
                // },
                data: { breadcrumb: 'Catering' },
                loadChildren: () =>
                    import('./catering/catering.module').then(
                        (m) => m.CateringModule
                    ),
            },

            {
                path: 'signage',
                data: { breadcrumb: 'Signage screen' },
                loadChildren: () =>
                    import('./signage-screen/signage-screen.module').then(
                        (m) => m.SignageScreenModule
                    ),
            },
        ],
    },
    {
        path: 'auth',
        data: { breadcrumb: 'Auth' },
        loadChildren: () =>
            import('./demo/components/auth/auth.module').then(
                (m) => m.AuthModule
            ),
    },
    {
        path: 'landing',
        loadChildren: () =>
            import('./demo/components/landing/landing.module').then(
                (m) => m.LandingModule
            ),
    },
    {
        path: 'notfound',
        loadChildren: () =>
            import('./demo/components/notfound/notfound.module').then(
                (m) => m.NotfoundModule
            ),
    },
    {
        path: 'unit-selection',
        data: { breadcrumb: 'Unit list' },
        loadComponent: () =>
            import('./unit-list-page/unit-list-page.component').then(
                (m) => m.UnitListPageComponent
            ),
    },
    { path: '**', redirectTo: 'auth' },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, routerOptions)],
    exports: [RouterModule],
    providers: [ArticleAccountingService],
})
export class AppRoutingModule { }
